import { API_BASE_URL } from "../utils/constants";
import {
  createApiError,
  getApiErrorMessage,
  normalizeApiError,
} from "../utils/errors";
import {
  clearAuthSession,
  getStoredToken,
  setAuthNotice,
} from "./authStorage";

const DEFAULT_TIMEOUT_MS = 15000;

function buildUrl(path, query) {
  const normalizedBaseUrl = API_BASE_URL.endsWith("/")
    ? API_BASE_URL.slice(0, -1)
    : API_BASE_URL;
  const normalizedPath = String(path || "").startsWith("/")
    ? path
    : `/${path}`;
  const basePath = /^https?:\/\//i.test(path)
    ? path
    : `${normalizedBaseUrl}${normalizedPath}`;

  if (!query || !Object.keys(query).length) {
    return basePath;
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value == null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null && item !== "") {
          params.append(key, item);
        }
      });
      return;
    }

    params.set(key, value);
  });

  const queryString = params.toString();

  return queryString ? `${basePath}?${queryString}` : basePath;
}

function buildHeaders(extraHeaders = {}, options = {}) {
  const headers = { ...extraHeaders };
  const { includeJson = false, skipAuth = false } = options;
  const token = skipAuth ? "" : getStoredToken();

  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function combineSignals(externalSignal, timeoutMs) {
  const controller = new AbortController();
  let timeoutId = null;
  let removeExternalAbort = null;
  let timedOut = false;

  if (externalSignal) {
    const handleExternalAbort = () => {
      controller.abort(externalSignal.reason);
    };

    if (externalSignal.aborted) {
      controller.abort(externalSignal.reason);
    } else {
      externalSignal.addEventListener("abort", handleExternalAbort, {
        once: true,
      });
      removeExternalAbort = () =>
        externalSignal.removeEventListener("abort", handleExternalAbort);
    }
  }

  if (timeoutMs) {
    timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort(new DOMException("Request timed out", "TimeoutError"));
    }, timeoutMs);
  }

  const cleanup = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    if (removeExternalAbort) {
      removeExternalAbort();
    }
  };

  return {
    signal: controller.signal,
    cleanup,
    didTimeout: () => timedOut,
  };
}

async function parseResponse(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const text = await response.text();

  if (!text) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return text;
    }
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
}

function normalizeSuccessPayload(payload) {
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    "success" in payload &&
    "data" in payload
  ) {
    return {
      success: Boolean(payload.success),
      message: payload.message || "",
      data: payload.data ?? null,
      meta: payload.meta ?? {},
    };
  }

  return {
    success: true,
    message: "",
    data: payload,
    meta: {},
  };
}

function handleUnauthorized(url, options = {}) {
  clearAuthSession();

  if (!options.skipNotice) {
    setAuthNotice("Your session has expired. Please login again.");
  }
}

async function request(path, options = {}) {
  const {
    method = "GET",
    query,
    body,
    headers,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    allowEmpty = method !== "GET",
    skipAuth = false,
  } = options;

  const url = buildUrl(path, query);
  const { signal: mergedSignal, cleanup, didTimeout } = combineSignals(
    signal,
    timeoutMs,
  );

  try {
    const response = await fetch(url, {
      method,
      cache: "no-store",
      headers,
      body,
      signal: mergedSignal,
    });

    const payload = await parseResponse(response);

    if (!response.ok) {
      const message = getApiErrorMessage(payload, response.status);
      const apiError = createApiError(message, {
        status: response.status,
        code: "HTTP_ERROR",
        url,
        details: payload,
      });

      if (response.status === 401 && !skipAuth) {
        handleUnauthorized(url, {
          skipNotice: /\/api\/auth\/login$/i.test(url),
        });
      }

      throw apiError;
    }

    const normalizedPayload = normalizeSuccessPayload(payload);

    if (!allowEmpty && (normalizedPayload.data == null || normalizedPayload.data === "")) {
      throw createApiError("No data returned.", {
        status: response.status,
        code: "EMPTY_RESPONSE",
        url,
      });
    }

    return normalizedPayload;
  } catch (error) {
    if (didTimeout()) {
      throw createApiError("Request timed out.", {
        code: "TIMEOUT",
        url,
        cause: error,
      });
    }

    throw normalizeApiError(error, { url });
  } finally {
    cleanup();
  }
}

export function get(path, options = {}) {
  return request(path, {
    ...options,
    method: "GET",
    headers: buildHeaders(options.headers, {
      skipAuth: options.skipAuth,
    }),
    allowEmpty: options.allowEmpty ?? false,
  });
}

export function post(path, options = {}) {
  const { body, ...rest } = options;

  return request(path, {
    ...rest,
    method: "POST",
    headers: buildHeaders(rest.headers, {
      includeJson: body !== undefined,
      skipAuth: rest.skipAuth,
    }),
    body: body != null ? JSON.stringify(body) : undefined,
    allowEmpty: rest.allowEmpty ?? true,
  });
}

export function patch(path, options = {}) {
  const { body, ...rest } = options;

  return request(path, {
    ...rest,
    method: "PATCH",
    headers: buildHeaders(rest.headers, {
      includeJson: body !== undefined,
      skipAuth: rest.skipAuth,
    }),
    body: body != null ? JSON.stringify(body) : undefined,
    allowEmpty: rest.allowEmpty ?? true,
  });
}

export function put(path, options = {}) {
  const { body, ...rest } = options;

  return request(path, {
    ...rest,
    method: "PUT",
    headers: buildHeaders(rest.headers, {
      includeJson: body !== undefined,
      skipAuth: rest.skipAuth,
    }),
    body: body != null ? JSON.stringify(body) : undefined,
    allowEmpty: rest.allowEmpty ?? true,
  });
}

export function del(path, options = {}) {
  return request(path, {
    ...options,
    method: "DELETE",
    headers: buildHeaders(options.headers, {
      skipAuth: options.skipAuth,
    }),
    allowEmpty: options.allowEmpty ?? true,
  });
}

export function upload(path, options = {}) {
  const { formData, ...rest } = options;

  return request(path, {
    ...rest,
    method: "POST",
    headers: buildHeaders(rest.headers, {
      skipAuth: rest.skipAuth,
    }),
    body: formData,
    allowEmpty: rest.allowEmpty ?? true,
  });
}

export function unwrapData(response) {
  return response?.data ?? null;
}

export async function getAllItems(path, options = {}) {
  const query = { ...(options.query || {}) };
  const pageSize = Math.min(Number(query.limit || 100) || 100, 100);
  let page = 1;
  let items = [];
  let lastMeta = {};

  while (page <= 100) {
    const response = await get(path, {
      ...options,
      query: {
        ...query,
        page,
        limit: pageSize,
      },
    });
    const data = unwrapData(response) || {};
    const pageItems = Array.isArray(data)
      ? data
      : Array.isArray(data.items)
        ? data.items
        : [];

    items = [...items, ...pageItems];
    lastMeta = response?.meta || {};

    if (!lastMeta?.totalPages || page >= lastMeta.totalPages) {
      break;
    }

    page += 1;
  }

  return {
    items,
    meta: lastMeta,
  };
}

const apiClient = {
  get,
  post,
  patch,
  put,
  del,
  upload,
  unwrapData,
  getAllItems,
};

export default apiClient;
